/**
 * **台本を作る層の入口**（Issue #766。索引 #761。設計 `docs/game-server.md`「ゲームサーバーの外との約束」）。
 *
 * ```
 * 台本を作る層（ここ）                  口                       描く層
 * イベント列・盤面の変化・記録  →  台本（timeline）  ＋ 時計 → frameAt(ms) →  duelStageLayers（duelEditor/stage・canvas）
 *  duelSituations / statChanges /                                         pages/useDuelPicture.js
 *  materialSummon / duelStart /
 *  fxObjects / fxScene / hudState
 * ```
 *
 * **台本**＝演出の種類・引数・始まりと長さ（`timeline.items`）・その時刻の盤面と HUD と舞台の設定（`timeline.states`）・SE のきっかけ（`timeline.cues`）。
 * 中身は今までの `duelEditor/timeline` の editor そのもの（作り直していない）。描く層が受け取るのは `frameAt(ms)` の値だけで、
 * 台本の作り方（並べ方 `sequential`・状況の分け方）を知らない。描く層を three から別のエンジンへ替えるときは、ここから下を替えない。
 *
 * - `/duel-timing`・`/duel-editor`（`pages/useDuelStage.js`）は、ここを通る
 * - **React も DOM も使わない**（Node でも動く）。時計も読まない（再生位置は `connectClock` が editor に押し込む）
 * - 境界は `stageScript.boundary.test.js` が見張る（ここから描く層の実装へ import しない・描く層の入口からここへ import しない）
 */
import { createEditor } from '../duelEditor/timeline/editor';
import { createRegistry } from '../duelEditor/timeline/types';
import { sourceOptions } from '../duelEditor/timeline/ports/source';
import { THEMES } from '../duelEditor/themes';
import { duelEffectContext, duelSpecs, themePicker } from './duelEffects';
import { duelSourceFor } from './duelStage';

/**
 * 台本を 1 つ作る。
 *
 * @param getTheme 今のテーマを返す関数（演出の型は全テーマの和で 1 回だけ作り、中身は**そのとき**のテーマから引く。Issue #541）
 * @returns `{ editor, setMatch, frameAt, timeline }`。`editor` は位置合わせの画面が文書を編集するのに使う（台本の持ち主）
 */
export function createDuelScript({ getTheme }) {
  const editor = createEditor({
    registry: createRegistry(duelSpecs(THEMES, { pick: themePicker(getTheme) })),
  });
  const effectContext = duelEffectContext();
  return {
    editor,
    /**
     * 試合（一式でも、増えていくイベント列の今までの分でも同じ形）を台本にする。イベントが無ければ何もしない。
     *
     * @param state `OcgGetState` の形（`events` / `board_steps` / `card_info`）
     * @param replay `OcgGetReplay` の形（`ops`。答えた時刻）
     * @param head `{ startVideoMs, firstPlayer, startLp }`
     * @param players プレイヤーの情報
     * @returns 台本を作ったか
     */
    setMatch({
      state = null, replay = null, head = null, players = [],
    } = {}) {
      const source = duelSourceFor({
        state, replay, head, players, getTheme, effectContext,
      });
      if (!source) return false;
      editor.setSource(sourceOptions(source));
      return true;
    },
    /** その時刻の台本（描く層が受け取る唯一の値） */
    frameAt: (ms) => editor.frameAt(ms),
    get timeline() { return editor.timeline; },
  };
}

/**
 * **応答（グループ）ごとの見せ終わりの時刻**（見せ終わり待ちのため。Issue #766）。
 *
 * 演出の長さを知っているのは台本（並べ方 `sequential` が前の帯を避けてずらした後の帯の終わり）なので、ここで出す。
 * 応答のイベントの時刻（整列後）と、そのイベントから立った帯の終わりの大きいほう。帯の無い応答はイベントの時刻。
 * 状態（`layer: 'state'`。チェーンの列など、後ろのアンカーで閉じるもの）は帯ではないので数えない。
 *
 * @param timeline `editor.timeline`
 * @returns `Map<groupId, endMs>`（groupId は op_uuid）
 */
export function groupEnds(timeline) {
  const out = new Map();
  const bump = (group, ms) => {
    if (group == null || !Number.isFinite(ms)) return;
    out.set(group, Math.max(out.get(group) ?? -Infinity, ms));
  };
  const byKey = timeline?.byKey ?? new Map();
  (timeline?.anchors ?? []).forEach((row) => bump(row.groupId, row.ms));
  (timeline?.items ?? []).forEach((item) => {
    const row = byKey.get?.(item.source?.anchorKey);
    bump(row?.groupId, item.endMs);
  });
  return out;
}
